/**
 * useSafeListening Hook
 * React hook for safe listening volume management
 * 
 * Usage:
 * const { 
 *   safeVolume,      // Current safe volume (capped if needed)
 *   applyVolume,     // Function to apply volume with limits
 *   status,          // Current status: 'safe' | 'warning' | 'danger'
 *   showBadge,       // Whether to show the safe listening badge
 *   badgeText,       // Text for the badge
 * } = useSafeListening(volume, lang);
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import safeListeningService, { SOFT_CAP_PERCENT, HARD_CAP_PERCENT } from '../services/safeListeningService';
import { toast } from 'sonner';

const useSafeListening = (currentVolume, lang = 'fr') => {
  const [status, setStatus] = useState('safe');
  const [showBadge, setShowBadge] = useState(false);
  const toastShownRef = useRef(false);
  
  // Update status when volume changes
  useEffect(() => {
    const { level } = safeListeningService.getStatus(currentVolume);
    setStatus(level);
    setShowBadge(level !== 'safe');
  }, [currentVolume]);
  
  // Apply volume limits and return safe volume
  const applyVolume = useCallback((requestedVolume) => {
    const result = safeListeningService.applyLimits(requestedVolume, lang);
    
    // Show toast for warning (once per session)
    if (result.warning && result.message && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.warning(result.message, {
        duration: 4000,
        icon: '🔊'
      });
    }
    
    // Show toast for cap reached (every time)
    if (result.capped && result.message) {
      toast.info(
        lang === 'fr' 
          ? 'Volume ajusté pour votre confort d\'écoute'
          : 'Volume adjusted for your listening comfort',
        {
          duration: 2000,
          icon: '🛡️'
        }
      );
    }
    
    return result.volume;
  }, [lang]);
  
  // Reset session state
  const resetSession = useCallback(() => {
    safeListeningService.resetSession();
    toastShownRef.current = false;
  }, []);
  
  // Get badge text based on status
  const getBadgeText = useCallback(() => {
    if (status === 'danger') {
      return lang === 'fr' ? 'Intensité maximale' : 'Maximum intensity';
    }
    if (status === 'warning') {
      return lang === 'fr' ? 'Intensité élevée' : 'High intensity';
    }
    return lang === 'fr' ? 'Écoute apaisante' : 'Soothing listening';
  }, [status, lang]);
  
  // Get badge color based on status
  const getBadgeColor = useCallback(() => {
    switch (status) {
      case 'danger':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'warning':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      default:
        return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
  }, [status]);
  
  // Get badge icon based on status
  const getBadgeIcon = useCallback(() => {
    switch (status) {
      case 'danger':
        return '⚠️';
      case 'warning':
        return '🔊';
      default:
        return '✓';
    }
  }, [status]);
  
  return {
    safeVolume: Math.min(currentVolume, HARD_CAP_PERCENT),
    applyVolume,
    status,
    showBadge,
    badgeText: getBadgeText(),
    badgeColor: getBadgeColor(),
    badgeIcon: getBadgeIcon(),
    resetSession,
    softCap: SOFT_CAP_PERCENT,
    hardCap: HARD_CAP_PERCENT,
    isAboveSoftCap: currentVolume >= SOFT_CAP_PERCENT,
    isAtHardCap: currentVolume >= HARD_CAP_PERCENT,
  };
};

export default useSafeListening;
